import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase-config';
import PageMeta from './PageMeta';
import ForAudienceNavStrip from './ForAudienceNavStrip';

const FLOW_STEPS = [
  {
    id: 'dashboard',
    label: '1. Dashboard',
    title: 'Start from your overview',
    copy: 'See what is waiting, open Create application when you are ready—same tabs as the live product.',
  },
  {
    id: 'intake',
    label: '2. Upload PDF',
    title: 'Drop the application PDF',
    copy: 'Upload triggers extraction; you review fields before anything is sent to an owner.',
  },
  {
    id: 'handoff',
    label: '3. Route to owner',
    title: 'Pick the homeowner',
    copy: 'Choose a connected owner or enter their email so the packet lands with the right decision-maker.',
  },
  {
    id: 'track',
    label: '4. My Applications',
    title: 'Status in one list',
    copy: 'Every packet—names, status, routing, View PDF—without digging through threads.',
  },
  {
    id: 'relationships',
    label: '5. Relationships',
    title: 'After conversion',
    copy: 'By Tenants / By Owners, search the portfolio, open lease and contact details in one place.',
  },
];

const FLOW_AUTO_ADVANCE_MS = 8000;

const FAQ_ITEMS = [
  {
    q: 'Do owners need an account?',
    a: 'Invited owners can accept and work inside Tenant Dispatch. You run intake; they see what they need to review or approve.',
  },
  {
    q: 'Where do PDFs live?',
    a: 'With your account for the workflow. Follow your brokerage and local rules for applicant PII.',
  },
  {
    q: 'Is this a CRM?',
    a: 'No—rental application intake, extraction, and owner handoff. It sits beside your existing tools.',
  },
  {
    q: 'How do I get access?',
    a: 'Get started with your work email. We reply with onboarding steps and timing for early-access cohorts.',
  },
];

function RealtorFlowMock({ stepId }) {
  const rows = {
    dashboard: (
      <>
        <div className="pro-landing-flow-row">
          <span className="pro-landing-flow-tag">Dashboard</span>
          <span><strong>2</strong> awaiting review · <strong>1</strong> vacant unit</span>
        </div>
        <div className="pro-landing-flow-row">
          <span className="pro-landing-flow-tag pro-landing-flow-tag--blue">Next</span>
          <span>Open <strong>Create application</strong> → PDF upload</span>
        </div>
      </>
    ),
    intake: (
      <>
        <div className="pro-landing-flow-row">
          <span className="pro-landing-flow-tag">PDF</span>
          <span><strong>RentalApp_Smith.pdf</strong> · Extracting…</span>
        </div>
        <div className="pro-landing-flow-row">
          <span className="pro-landing-flow-tag pro-landing-flow-tag--amber">Review</span>
          <span>Income, employer, refs filled—verify then continue</span>
        </div>
      </>
    ),
    handoff: (
      <>
        <div className="pro-landing-flow-row">
          <span className="pro-landing-flow-tag">Owner</span>
          <span><strong>Jordan Lee</strong> (connected) · or invite by email</span>
        </div>
        <div className="pro-landing-flow-row">
          <span className="pro-landing-flow-tag pro-landing-flow-tag--violet">Send</span>
          <span>Create application → owner notified</span>
        </div>
      </>
    ),
    track: (
      <>
        <div className="pro-landing-flow-row">
          <span className="pro-landing-flow-tag">My Apps</span>
          <span><strong>Smith</strong> · Pending review · View PDF</span>
        </div>
        <div className="pro-landing-flow-row">
          <span className="pro-landing-flow-tag pro-landing-flow-tag--blue">Row</span>
          <span><strong>Garcia</strong> · Approved · Owner: Lee</span>
        </div>
      </>
    ),
    relationships: (
      <>
        <div className="pro-landing-flow-row">
          <span className="pro-landing-flow-tag">View</span>
          <span><strong>By Tenants</strong> / By Owners · search portfolio</span>
        </div>
        <div className="pro-landing-flow-row">
          <span className="pro-landing-flow-tag pro-landing-flow-tag--violet">Card</span>
          <span>Unit 12B · Tenant + owner · lease summary</span>
        </div>
      </>
    ),
  };

  return (
    <div className="pro-landing-flow-mock" aria-hidden>
      <div className="pro-landing-flow-mock-header">
        <span className="pro-landing-flow-mock-title">Realtor preview</span>
      </div>
      {rows[stepId] || rows.dashboard}
    </div>
  );
}

const RealtorLandingPage = () => {
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [navSolid, setNavSolid] = useState(false);
  const [showStickyCta, setShowStickyCta] = useState(false);
  const [flowStep, setFlowStep] = useState(0);
  const [faqOpen, setFaqOpen] = useState(null);
  const [accessRequestStatus, setAccessRequestStatus] = useState('idle');
  const [accessRequestMessage, setAccessRequestMessage] = useState('');

  const handleRequestAccess = useCallback(async () => {
    const trimmed = email.trim();
    if (!trimmed) {
      setAccessRequestStatus('error');
      setAccessRequestMessage('Please enter your work email.');
      return;
    }

    setAccessRequestStatus('loading');
    setAccessRequestMessage('');

    try {
      const submit = httpsCallable(functions, 'submitRealtorAccessRequest');
      const result = await submit({
        email: trimmed,
        notes: notes.trim() || undefined,
      });
      const msg = result.data?.message || 'Request sent. Check your inbox.';
      setAccessRequestStatus('success');
      setAccessRequestMessage(msg);
    } catch (err) {
      setAccessRequestStatus('error');
      const fallback =
        'Could not send your request. Please try again or email hello@tenantdispatch.com.';
      const code = err?.code || '';
      const raw = (err?.message || '').trim();
      if (code === 'functions/invalid-argument') {
        setAccessRequestMessage(
          raw && raw.length < 200 ? raw : 'Please enter a valid work email.'
        );
      } else {
        setAccessRequestMessage(
          raw && raw.length > 5 && raw.length < 240 ? raw : fallback
        );
      }
    }
  }, [email, notes]);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (accessRequestStatus === 'error' || accessRequestStatus === 'success') {
      setAccessRequestStatus('idle');
      setAccessRequestMessage('');
    }
  };

  const handleNotesChange = (e) => {
    setNotes(e.target.value.slice(0, 500));
    if (accessRequestStatus === 'error' || accessRequestStatus === 'success') {
      setAccessRequestStatus('idle');
      setAccessRequestMessage('');
    }
  };

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      setFlowStep((s) => (s + 1) % FLOW_STEPS.length);
    }, FLOW_AUTO_ADVANCE_MS);
    return () => clearInterval(id);
  }, [flowStep]);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setNavSolid(y > 56);
      setShowStickyCta(y > 420);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const step = FLOW_STEPS[flowStep];

  return (
    <div className="realtor-landing-page">
      <PageMeta
        title="Tenant Dispatch for Realtors - Rental Applications & Owner Handoff"
        description="Upload rental PDFs, extract applicant fields, route to property owners, and track every packet in one pipeline—built for leasing workflows, not generic CRMs."
        keywords="realtor software, rental application management, tenant screening PDF, property management tools, real estate technology"
      />

      <header className={`realtor-nav ${navSolid ? 'realtor-nav--solid' : 'realtor-nav--hero'}`}>
        <div className="realtor-nav-inner">
          <div className="realtor-nav-start">
            <Link to="/" className="realtor-nav-brand" aria-label="Tenant Dispatch — back to home">
              <img src={`${process.env.PUBLIC_URL}/tenant-dispatch-icon-192.png`} alt="" className="realtor-nav-logo" width={36} height={36} />
              <span className="realtor-nav-brand-text">For Realtors</span>
            </Link>
            <ForAudienceNavStrip variant="realtor" activeAudience="realtors" />
          </div>
          <nav className="realtor-nav-links" aria-label="Page sections">
            <button type="button" onClick={() => scrollToId('realtor-flow')}>Flow</button>
            <button type="button" onClick={() => scrollToId('realtor-bento')}>Capabilities</button>
            <button type="button" onClick={() => scrollToId('realtor-access')}>Access</button>
            <button type="button" onClick={() => scrollToId('realtor-compare')}>Compare</button>
            <button type="button" onClick={() => scrollToId('realtor-faq')}>FAQ</button>
          </nav>
          <button type="button" className="realtor-nav-cta" onClick={() => scrollToId('realtor-cta')}>
            Get started
          </button>
        </div>
      </header>

      <main className="realtor-landing-main">
        <section id="realtor-hero" className="realtor-hero">
          <div className="realtor-hero-content">
            <div className="realtor-badge">📋 Rental applications, not a generic CRM</div>
            <h1 className="realtor-hero-title">
              PDFs to{' '}
              <span className="realtor-hero-accent">owner-ready packages</span>
              {' '}in one pipeline
            </h1>
            <p className="realtor-hero-subtitle">
              Upload a rental application, review extracted fields, route to the right homeowner, and track status—so you retype less and chase PDFs less.
            </p>
            <div className="realtor-hero-cta">
              <div className="realtor-email-input">
                <input
                  type="email"
                  placeholder="Work email"
                  value={email}
                  onChange={handleEmailChange}
                  onKeyDown={(e) => e.key === 'Enter' && accessRequestStatus !== 'loading' && handleRequestAccess()}
                  aria-label="Work email"
                  disabled={accessRequestStatus === 'loading'}
                />
                <button
                  type="button"
                  onClick={handleRequestAccess}
                  className="realtor-cta-primary"
                  disabled={accessRequestStatus === 'loading'}
                >
                  {accessRequestStatus === 'loading' ? 'Sending…' : (
                    <>
                      Get started
                      <span className="cta-arrow" aria-hidden>→</span>
                    </>
                  )}
                </button>
              </div>
              <div className="pro-landing-notes-wrap">
                <label htmlFor="realtor-landing-notes">Brokerage &amp; market (optional)</label>
                <textarea
                  id="realtor-landing-notes"
                  className="pro-landing-notes"
                  placeholder="e.g. ABC Realty, Greater Austin leasing team"
                  value={notes}
                  onChange={handleNotesChange}
                  rows={3}
                  disabled={accessRequestStatus === 'loading'}
                  maxLength={500}
                />
                <p className="pro-landing-notes-hint">Helps us route your request. Max 500 characters.</p>
              </div>
              <div className="realtor-hero-actions-row">
                <button type="button" className="realtor-hero-secondary" onClick={() => scrollToId('realtor-flow')}>
                  See the workflow
                </button>
              </div>
              <p className="realtor-cta-note">Early access • We reply by email • No credit card to get started</p>
              {accessRequestStatus === 'loading' && (
                <p className="realtor-access-feedback realtor-access-feedback--hero realtor-access-feedback--loading" aria-live="polite">
                  Sending your request…
                </p>
              )}
              {(accessRequestStatus === 'success' || accessRequestStatus === 'error') && accessRequestMessage && (
                <p
                  role="status"
                  className={`realtor-access-feedback realtor-access-feedback--hero ${accessRequestStatus === 'success' ? 'realtor-access-feedback--success' : 'realtor-access-feedback--error'}`}
                >
                  {accessRequestMessage}
                </p>
              )}
            </div>
          </div>
          <div className="realtor-hero-visual">
            <div className="realtor-dashboard-preview" aria-hidden>
              <div className="preview-header">
                <div className="preview-dots">
                  <span /><span /><span />
                </div>
                <span className="preview-title">Realtor workflow</span>
              </div>
              <div className="realtor-preview-content">
                <div className="preview-card green">
                  <span className="card-icon">📄</span>
                  <span>PDF in → fields extracted</span>
                </div>
                <div className="preview-card blue">
                  <span className="card-icon">👤</span>
                  <span>Route to owner</span>
                </div>
                <div className="preview-card yellow">
                  <span className="card-icon">📋</span>
                  <span>My Applications · status</span>
                </div>
                <div className="preview-card purple">
                  <span className="card-icon">🔗</span>
                  <span>Tenants ↔ owners in one place</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="realtor-trust-strip" role="presentation">
          <span>PDF intake</span>
          <span className="realtor-trust-dot" aria-hidden>·</span>
          <span>Owner routing</span>
          <span className="realtor-trust-dot" aria-hidden>·</span>
          <span>Live status</span>
          <span className="realtor-trust-dot" aria-hidden>·</span>
          <span>Post-lease relationships</span>
        </div>

        <section className="realtor-paths" aria-label="Choose what to explore">
          <div className="realtor-paths-inner">
            <button type="button" className="realtor-path-card" onClick={() => scrollToId('realtor-flow')}>
              <span className="realtor-path-card-kicker">See the product path</span>
              <span className="realtor-path-card-title">Step through the flow</span>
            </button>
            <button type="button" className="realtor-path-card" onClick={() => scrollToId('realtor-bento')}>
              <span className="realtor-path-card-kicker">What you get</span>
              <span className="realtor-path-card-title">Capabilities</span>
            </button>
            <button type="button" className="realtor-path-card" onClick={() => scrollToId('realtor-faq')}>
              <span className="realtor-path-card-kicker">Questions</span>
              <span className="realtor-path-card-title">FAQ</span>
            </button>
          </div>
        </section>

        <section id="realtor-flow" className="realtor-flow-section">
          <div className="section-header">
            <span className="section-badge">How it works</span>
            <h2 className="section-title">{"The workflow you'll follow in Tenant Dispatch"}</h2>
            <p className="section-subtitle">
              Plain-language preview below—auto-advances every few seconds, or pick a step. No screenshots required.
            </p>
          </div>
          <div className="realtor-flow-layout">
            <div className="realtor-flow-steps" aria-label="Flow steps">
              {FLOW_STEPS.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  aria-pressed={flowStep === i}
                  className={`realtor-flow-step-btn ${flowStep === i ? 'is-active' : ''}`}
                  onClick={() => setFlowStep(i)}
                >
                  <span className="realtor-flow-step-label">{s.label}</span>
                  <span className="realtor-flow-step-title">{s.title}</span>
                </button>
              ))}
            </div>
            <div className="realtor-flow-panel">
              <div className="realtor-flow-panel-chrome">
                <div className="realtor-flow-panel-dots"><span /><span /><span /></div>
                <span className="realtor-flow-panel-title">Tenant Dispatch</span>
              </div>
              <div className="realtor-flow-panel-body">
                <div
                  key={step.id}
                  className="realtor-flow-step-transition"
                  aria-live="polite"
                >
                  <h3 className="realtor-flow-panel-heading">{step.title}</h3>
                  <p className="realtor-flow-panel-copy">{step.copy}</p>
                  <div className={`realtor-flow-visual realtor-flow-visual--${step.id}`}>
                    <RealtorFlowMock stepId={step.id} />
                  </div>
                  <div className="realtor-flow-nav">
                    <button
                      type="button"
                      className="realtor-flow-back"
                      disabled={flowStep === 0}
                      onClick={() => setFlowStep((x) => Math.max(0, x - 1))}
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      className="realtor-flow-next"
                      disabled={flowStep === FLOW_STEPS.length - 1}
                      onClick={() => setFlowStep((x) => Math.min(FLOW_STEPS.length - 1, x + 1))}
                    >
                      Next step
                    </button>
                  </div>
                  <p className="realtor-flow-auto-hint">
                    Auto-advances every {FLOW_AUTO_ADVANCE_MS / 1000}s while this tab is visible, then loops. Respects reduced motion.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="realtor-bento" className="realtor-bento-section">
          <div className="section-header">
            <span className="section-badge">Capabilities</span>
            <h2 className="section-title">Built for the handoff, not feature sprawl</h2>
            <p className="section-subtitle">Everything ties to one job: get clean applications to owners faster.</p>
          </div>
          <div className="realtor-bento">
            <article className="realtor-bento-tile realtor-bento-tile--hero">
              <div className="realtor-bento-icon">🤖</div>
              <h3>Extraction you verify</h3>
              <p>Structured fields from the PDF—accuracy depends on the file; you confirm before send.</p>
            </article>
            <article className="realtor-bento-tile">
              <div className="realtor-bento-icon">👥</div>
              <h3>Owner routing</h3>
              <p>Invites and connections so packets reach the right person.</p>
            </article>
            <article className="realtor-bento-tile">
              <div className="realtor-bento-icon">📋</div>
              <h3>Pipeline &amp; status</h3>
              <p>Pending, follow-up, approved—one dashboard instead of inbox archaeology.</p>
            </article>
            <article className="realtor-bento-tile realtor-bento-tile--wide">
              <div className="realtor-bento-icon">📱</div>
              <h3>Works on the go</h3>
              <p>Upload or check status from your phone between showings.</p>
            </article>
          </div>
        </section>

        <section id="realtor-access" className="realtor-early-section">
          <div className="section-header">
            <span className="section-badge">Access</span>
            <h2 className="section-title">How early access works</h2>
            <p className="section-subtitle">
              Small cohorts while we tune the product—straightforward steps, no mystery.
            </p>
          </div>
          <div className="realtor-compare-table-wrap" style={{ maxWidth: 720 }}>
            <table className="realtor-compare-table">
              <thead>
                <tr>
                  <th scope="col">Step</th>
                  <th scope="col">What happens</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">1. Get started</th>
                  <td>Work email + optional brokerage note. We confirm we received you.</td>
                </tr>
                <tr>
                  <th scope="row">2. Review</th>
                  <td>We fit you into an onboarding cohort and reply with timing and next actions.</td>
                </tr>
                <tr>
                  <th scope="row">3. Go live</th>
                  <td>You get access to the realtor dashboard and can run real workflows with us.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section id="realtor-compare" className="realtor-compare-section">
          <div className="section-header">
            <span className="section-badge">Reality check</span>
            <h2 className="section-title">PDFs in email vs. one connected pipeline</h2>
            <p className="section-subtitle">We are not replacing your CRM—we tighten rental application handoff on Tenant Dispatch.</p>
          </div>
          <div className="realtor-compare-table-wrap">
            <table className="realtor-compare-table">
              <thead>
                <tr>
                  <th scope="col">Topic</th>
                  <th scope="col">Most offices today</th>
                  <th scope="col">On Tenant Dispatch</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">Intake</th>
                  <td>Retype from PDF into email or sheets</td>
                  <td>Upload → extract → you verify</td>
                </tr>
                <tr>
                  <th scope="row">Owner</th>
                  <td>Long threads, missing context</td>
                  <td>Routing tied to the application record</td>
                </tr>
                <tr>
                  <th scope="row">Status</th>
                  <td>Manual follow-ups</td>
                  <td>Statuses in one place</td>
                </tr>
                <tr>
                  <th scope="row">Scope</th>
                  <td>General tools</td>
                  <td>Rental apps + owner coordination</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="realtor-security-section">
          <div className="realtor-security-inner">
            <h2 className="realtor-security-title">Applicant data</h2>
            <p className="realtor-security-copy">
              You handle sensitive PII. Use Tenant Dispatch with your brokerage policies and local privacy rules in mind.
            </p>
          </div>
        </section>

        <section id="realtor-faq" className="realtor-faq-section">
          <div className="section-header">
            <span className="section-badge">FAQ</span>
            <h2 className="section-title">Short answers</h2>
            <p className="section-subtitle">Tap to expand. Ask us anything before you get started.</p>
          </div>
          <div className="realtor-faq-list">
            {FAQ_ITEMS.map((item, i) => (
              <div key={item.q} className={`realtor-faq-item ${faqOpen === i ? 'is-open' : ''}`}>
                <button
                  type="button"
                  className="realtor-faq-trigger"
                  aria-expanded={faqOpen === i}
                  onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                >
                  <span>{item.q}</span>
                  <span className="realtor-faq-chevron" aria-hidden>{faqOpen === i ? '−' : '+'}</span>
                </button>
                {faqOpen === i && <div className="realtor-faq-answer">{item.a}</div>}
              </div>
            ))}
          </div>
        </section>

        <section id="realtor-cta" className="realtor-final-cta">
          <div className="final-cta-content">
            <h2>Get started</h2>
            <p>Work email—we follow up with onboarding options and timing.</p>
            <div className="final-cta-email-row">
              <input
                type="email"
                placeholder="Work email"
                value={email}
                onChange={handleEmailChange}
                onKeyDown={(e) => e.key === 'Enter' && accessRequestStatus !== 'loading' && handleRequestAccess()}
                className="realtor-final-email"
                aria-label="Work email"
                disabled={accessRequestStatus === 'loading'}
              />
              <button
                type="button"
                onClick={handleRequestAccess}
                className="realtor-cta-large"
                disabled={accessRequestStatus === 'loading'}
              >
                {accessRequestStatus === 'loading' ? 'Sending…' : (
                  <>
                    Get started
                    <span className="cta-arrow" aria-hidden>→</span>
                  </>
                )}
              </button>
            </div>
            <textarea
              className="pro-landing-final-notes"
              placeholder="Optional: brokerage, market, team size"
              value={notes}
              onChange={handleNotesChange}
              rows={3}
              disabled={accessRequestStatus === 'loading'}
              maxLength={500}
              aria-label="Optional brokerage details"
            />
            {accessRequestStatus === 'loading' && (
              <p className="realtor-access-feedback realtor-access-feedback--final realtor-access-feedback--loading" aria-live="polite">
                Sending your request…
              </p>
            )}
            {(accessRequestStatus === 'success' || accessRequestStatus === 'error') && accessRequestMessage && (
              <p
                role="status"
                className={`realtor-access-feedback realtor-access-feedback--final ${accessRequestStatus === 'success' ? 'realtor-access-feedback--success' : 'realtor-access-feedback--error'}`}
              >
                {accessRequestMessage}
              </p>
            )}
            <p className="final-cta-note">hello@tenantdispatch.com • Replies from our team</p>
          </div>
        </section>
      </main>

      <div className={`realtor-sticky-cta ${showStickyCta ? 'is-visible' : ''}`} role="region" aria-label="Call to action">
        <div className="realtor-sticky-cta-inner">
          <span className="realtor-sticky-cta-label">Tenant Dispatch for Realtors</span>
          <button type="button" className="realtor-sticky-cta-btn" onClick={() => scrollToId('realtor-cta')}>
            Get started
          </button>
        </div>
      </div>
    </div>
  );
};

export default RealtorLandingPage;
